import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Upload, Trash2, ChevronLeft, ChevronRight, Lock, Eye, Volume2, Sun, Users, Box, Compass, Sparkles, Wind, Thermometer } from 'lucide-react';
import { motion } from 'framer-motion';

// Area environment options
const AREA_ENVIRONMENTS = ['Indoor', 'Outdoor', 'Mixed'];

// ==================== PROFESSIONAL AUDITOR-STYLE CATEGORY HEADERS ====================
const CATEGORY_HEADERS = {
  'Noise': {
    title: 'Sound Environment',
    question: 'How would you describe the sound environment in this area?',
    helper: 'Consider background noise, voices, and any sudden or repetitive sounds.',
    icon: Volume2
  },
  'Lighting': {
    title: 'Lighting Conditions',
    question: 'How would you describe the lighting in this area?',
    helper: 'Think about brightness levels, natural vs artificial light, and any flickering.',
    icon: Sun
  },
  'Crowds & Movement': {
    title: 'Activity & Crowding',
    question: 'How busy or crowded does this area typically become?',
    helper: 'Consider the number of people and movement during peak times.',
    icon: Users
  },
  'Smell / Air Quality': {
    title: 'Smell & Air Quality',
    question: 'How would you describe the air quality and any noticeable smells?',
    helper: 'Consider ventilation, food smells, cleaning products, or other scents.',
    icon: Wind
  },
  'Temperature / Weather': {
    title: 'Temperature Comfort',
    question: 'How comfortable is the temperature in this area?',
    helper: 'Think about heating, cooling, drafts, or outdoor conditions.',
    icon: Thermometer
  },
  'Spatial Layout': {
    title: 'Space & Layout',
    question: 'How would you describe the space and layout?',
    helper: 'Does it feel open and easy to move through, or more tight and enclosed?',
    icon: Box
  },
  'Visual Environment': {
    title: 'Visual Stimulation',
    question: 'How visually stimulating is this area?',
    helper: 'Think about displays, screens, patterns, movement, and how visually busy it feels.',
    icon: Sparkles
  }
};

// ==================== STANDARDIZED OPTION LABELS (Professional wording) ====================
// Maps original option labels to professional alternatives while preserving scores
const OPTION_LABEL_OVERRIDES = {
  // Noise
  'Very quiet / silent': 'Usually quiet',
  'Low background noise': 'Low activity',
  'Moderate background noise': 'Moderate',
  'High background noise': 'Busy / noisy',
  // Crowds
  'Very quiet / empty': 'Usually quiet',
  'Few people present': 'Low activity',
  'Moderately busy': 'Moderate',
  'Very busy': 'Busy / crowded',
  // Spatial
  'Very open / spacious': 'Open and spacious',
  'Open': 'Reasonably open',
  'Somewhat enclosed': 'Moderately enclosed',
  'Very enclosed': 'Tight / enclosed',
  // Visual
  'Very simple visual environment': 'Visually calm',
  'Some visual elements present': 'Some visual interest',
  'Visually complex environment': 'Visually busy',
  'Very visually complex environment': 'Very stimulating',
};

// Get display label for option (use override if exists, else original)
const getOptionDisplayLabel = (originalLabel) => {
  return OPTION_LABEL_OVERRIDES[originalLabel] || originalLabel;
};

// ==================== SENSORY PREVIEW GENERATOR ====================
const generateSensoryPreview = (responses, categories, prompts, areaEnvironment, filterPromptsByEnvironment) => {
  if (!responses || Object.keys(responses).length === 0) {
    return null;
  }

  // Calculate average scores per category
  const categoryScores = {};
  let totalScore = 0;
  let totalCount = 0;

  categories.forEach(cat => {
    const catPrompts = filterPromptsByEnvironment(prompts[cat] || [], areaEnvironment);
    let catTotal = 0;
    let catCount = 0;

    catPrompts.forEach(p => {
      const score = responses[`${cat}_${p.id}`];
      if (score !== undefined && score !== null) {
        catTotal += score;
        catCount++;
        totalScore += score;
        totalCount++;
      }
    });

    if (catCount > 0) {
      categoryScores[cat] = catTotal / catCount;
    }
  });

  if (totalCount === 0) return null;

  const avgScore = totalScore / totalCount;
  
  // Determine overall level (0-1: Low, 1-2: Moderate, 2-3: High)
  let level = 'Moderate';
  let color = '#f59e0b'; // amber
  if (avgScore < 1) {
    level = 'Low';
    color = '#22c55e'; // green
  } else if (avgScore >= 2) {
    level = 'High';
    color = '#ef4444'; // red
  }

  // Generate professional summary based on highest-impact categories
  // Priority: Crowds & Movement > Spatial Layout > Noise > others
  const crowdScore = categoryScores['Crowds & Movement'] || 0;
  const spaceScore = categoryScores['Spatial Layout'] || 0;
  const noiseScore = categoryScores['Noise'] || 0;

  let summary = '';
  
  if (crowdScore >= 2 && noiseScore >= 2) {
    summary = 'Can feel busy and moderately loud during peak times.';
  } else if (crowdScore >= 2) {
    summary = 'Can feel busy during peak times, but generally manageable.';
  } else if (spaceScore >= 2) {
    summary = 'May feel enclosed. Take your time moving through.';
  } else if (noiseScore >= 2) {
    summary = 'Can feel moderately loud at times.';
  } else if (crowdScore < 1 && noiseScore < 1) {
    summary = 'Generally calm, with minimal background noise.';
  } else {
    summary = 'Generally calm, with occasional background activity.';
  }

  return { level, color, summary, avgScore };
};

const AreaBuilder = () => {
  const { venueId, areaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [submissionId, setSubmissionId] = useState(location.state?.submission_id || null);
  const [prompts, setPrompts] = useState(null);
  const [assessmentIntro, setAssessmentIntro] = useState({});
  const [categoryDescriptions, setCategoryDescriptions] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [areaData, setAreaData] = useState({
    name: '',
    description: '',
    area_environment: '',
    area_summary: '',
    observations: '', // Observations and specific considerations field
    photos: [],
    support_indicators: {
      // Physical
      seating_available: false,
      step_free_access: false,
      accessible_toilet_nearby: false,
      // Staff
      staff_present: false,
      assistance_available: false,
      // Sensory
      quiet_space_nearby: false,
      lower_lighting_available: false,
      calm_waiting_area: false,
      // Navigation
      clear_signage: false,
      map_available: false,
      // Access Control
      timed_entry_available: false,
      managed_queue_system: false,
    },
  });
  const [responses, setResponses] = useState({});
  const [savedAreaId, setSavedAreaId] = useState(areaId || null);
  const [uploading, setUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [sensoryNotes, setSensoryNotes] = useState({});
  const [isLocked, setIsLocked] = useState(false);

  const categories = [
    'Noise',
    'Lighting',
    'Crowds & Movement',
    'Smell / Air Quality',
    'Temperature / Weather',
    'Spatial Layout',
    'Visual Environment',
  ];

  // Filter prompts based on area environment
  const filterPromptsByEnvironment = (promptList, areaEnvironment) => {
    if (!promptList) return [];
    
    return promptList.filter(prompt => {
      const env = prompt.environment || 'all';
      
      if (env === 'all') return true;
      
      // Match environment directly (case-sensitive: Indoor, Outdoor, Mixed)
      return env === areaEnvironment;
    });
  };

  useEffect(() => {
    fetchPrompts();
    if (areaId) {
      setSavedAreaId(areaId);
      fetchArea();
    }
    // Always get or create submission for this venue
    fetchOrCreateSubmission();
  }, []);

  const fetchOrCreateSubmission = async () => {
    if (venueId) {
      try {
        // First check if there's a locked submission for this venue
        const submissionsRes = await api.get(`/submissions/venue/${venueId}`);
        const lockedSubmission = submissionsRes.data.find(s => s.is_locked || s.status === 'locked');
        
        if (lockedSubmission) {
          setIsLocked(true);
          setSubmissionId(lockedSubmission.submission_id);
          toast.error('This venue has a locked submission. Editing is disabled.');
          return;
        }
        
        const response = await api.post('/submissions', { venue_id: venueId });
        setSubmissionId(response.data.submission_id);
        console.log(`Using submission ${response.data.submission_id} for venue ${venueId}`);
      } catch (error) {
        console.error('Failed to get/create submission:', error);
        toast.error('Failed to initialize submission');
      }
    }
  };

  const fetchPrompts = async () => {
    try {
      const response = await api.get('/prompts');
      setPrompts(response.data.prompts);
      setAssessmentIntro(response.data.assessment_intro || {});
      setCategoryDescriptions(response.data.category_descriptions || {});
    } catch (error) {
      toast.error('Failed to load prompts');
    }
  };

  const fetchArea = async () => {
    try {
      const response = await api.get(`/areas/${areaId}`);
      setAreaData({
        name: response.data.name,
        description: response.data.description || '',
        area_environment: response.data.area_environment || '',
        area_summary: response.data.area_summary || '',
        observations: response.data.observations || '',
        photos: response.data.photos || [],
        support_indicators: response.data.support_indicators || {
          seating_available: false,
          step_free_access: false,
          accessible_toilet_nearby: false,
          staff_present: false,
          assistance_available: false,
          quiet_space_nearby: false,
          lower_lighting_available: false,
          calm_waiting_area: false,
          clear_signage: false,
          map_available: false,
          timed_entry_available: false,
          managed_queue_system: false,
        },
      });
    } catch (error) {
      toast.error('Failed to load area');
    }
  };

  // Fetch existing responses when submissionId and areaId are available
  useEffect(() => {
    const fetchExistingResponses = async () => {
      if (!submissionId || !areaId) return;
      
      try {
        const response = await api.get(`/category-responses/area/${areaId}?submission_id=${submissionId}`);
        const existingResponses = {};
        
        response.data.forEach((cr) => {
          cr.prompt_responses.forEach((pr) => {
            existingResponses[`${cr.category_name}_${pr.prompt_id}`] = pr.score;
          });
        });
        
        if (Object.keys(existingResponses).length > 0) {
          setResponses(existingResponses);
          console.log('Loaded existing responses:', Object.keys(existingResponses).length);
        }
      } catch (error) {
        console.log('No existing responses found for this area');
      }
    };
    
    fetchExistingResponses();
  }, [submissionId, areaId]);

  const saveArea = async () => {
    try {
      const payload = {
        venue_id: venueId,
        type: 'custom',
        name: areaData.name,
        description: areaData.description,
        area_environment: areaData.area_environment,
        area_summary: areaData.area_summary,
        observations: areaData.observations,
        photos: areaData.photos,
        support_indicators: areaData.support_indicators,
      };

      let response;
      if (savedAreaId) {
        response = await api.patch(`/areas/${savedAreaId}`, payload);
      } else {
        response = await api.post('/areas', payload);
        setSavedAreaId(response.data.area_id);
      }
      return response.data.area_id;
    } catch (error) {
      toast.error('Failed to save area');
      throw error;
    }
  };

  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    // Increase limit to 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|heic|heif|webp)$/i)) {
      toast.error('Only JPEG, PNG, HEIC, or WebP images are allowed');
      return;
    }

    setUploading(true);

    try {
      // Get Cloudinary signature
      const signatureResponse = await api.get('/cloudinary/signature', {
        params: { folder: 'nysa/venues' }
      });

      const { signature, timestamp, cloud_name, api_key, folder } = signatureResponse.data;

      // Upload to Cloudinary - only include signed params
      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Cloudinary upload failed:', uploadResponse.status, errorText);
        throw new Error(`Upload failed: ${uploadResponse.status} - ${errorText}`);
      }

      const uploadData = await uploadResponse.json();

      if (uploadData.secure_url) {
        const newPhoto = {
          url: uploadData.secure_url,
          order: areaData.photos.length
        };

        // Use functional update to avoid stale state
        setAreaData(prevData => {
          const updatedPhotos = [...prevData.photos, newPhoto];
          
          // Auto-save area with new photo to persist immediately
          if (savedAreaId) {
            api.patch(`/areas/${savedAreaId}`, {
              venue_id: venueId,
              type: 'custom',
              name: prevData.name,
              description: prevData.description,
              photos: updatedPhotos,
              support_indicators: prevData.support_indicators,
            }).then(() => {
              console.log('Photo persisted to area:', savedAreaId);
            }).catch(saveErr => {
              console.error('Failed to auto-save photo:', saveErr);
              toast.error('Photo uploaded but failed to save - please save area manually');
            });
          }
          
          return {
            ...prevData,
            photos: updatedPhotos
          };
        });

        toast.success('Photo uploaded successfully!');
      } else {
        console.error('Cloudinary response missing secure_url:', uploadData);
        throw new Error(uploadData.error?.message || 'Upload failed - no URL returned');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      setUploadKey(prev => prev + 1); // Force input re-render on mobile
    }
  };

  const handleDeletePhoto = (index) => {
    const updatedPhotos = areaData.photos.filter((_, i) => i !== index);
    setAreaData({
      ...areaData,
      photos: updatedPhotos
    });
    
    // Auto-save if area exists
    if (savedAreaId) {
      api.patch(`/areas/${savedAreaId}`, {
        venue_id: venueId,
        type: 'custom',
        name: areaData.name,
        description: areaData.description,
        photos: updatedPhotos,
        support_indicators: areaData.support_indicators,
      }).catch(err => console.error('Failed to save after delete:', err));
    }
    
    toast.success('Photo removed');
  };

  const handleMovePhoto = (index, direction) => {
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= areaData.photos.length) return;
    
    const updatedPhotos = [...areaData.photos];
    const temp = updatedPhotos[index];
    updatedPhotos[index] = updatedPhotos[newIndex];
    updatedPhotos[newIndex] = temp;
    
    // Update order property
    updatedPhotos.forEach((photo, i) => {
      photo.order = i;
    });
    
    setAreaData({
      ...areaData,
      photos: updatedPhotos
    });
    
    // Auto-save if area exists
    if (savedAreaId) {
      api.patch(`/areas/${savedAreaId}`, {
        venue_id: venueId,
        type: 'custom',
        name: areaData.name,
        description: areaData.description,
        photos: updatedPhotos,
        support_indicators: areaData.support_indicators,
      }).catch(err => console.error('Failed to save after reorder:', err));
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!areaData.name.trim()) {
        toast.error('Please enter an area name');
        return;
      }
      
      if (!areaData.area_environment) {
        toast.error('Please select an area environment (Indoor/Outdoor/Mixed)');
        return;
      }
      
      // Validate description word count
      const wordCount = countWords(areaData.description);
      if (wordCount > 250) {
        toast.error(`Description too long (${wordCount}/250 words)`);
        return;
      }
      
      const aId = await saveArea();
      if (aId && !savedAreaId) {
        setSavedAreaId(aId);
      }
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      const categoryName = categories[currentCategory];
      const allCategoryPrompts = prompts[categoryName];
      const filteredPrompts = filterPromptsByEnvironment(allCategoryPrompts, areaData.area_environment);
      
      // Validate all filtered prompts have EXPLICIT responses (not undefined)
      const allAnswered = filteredPrompts.every(p => 
        responses[`${categoryName}_${p.id}`] !== undefined && 
        responses[`${categoryName}_${p.id}`] !== null
      );
      
      if (!allAnswered) {
        toast.error(`Please answer all ${filteredPrompts.length} questions before proceeding`);
        return;
      }
      
      const promptResponses = filteredPrompts.map((p) => ({
        prompt_id: p.id,
        score: responses[`${categoryName}_${p.id}`],
      }));

      const currentAreaId = savedAreaId || areaId;
      if (!currentAreaId || !submissionId) {
        toast.error('Missing area or submission ID. Please restart.');
        return;
      }

      try {
        await api.post('/category-responses', {
          area_id: currentAreaId,
          submission_id: submissionId,
          category_name: categoryName,
          prompt_responses: promptResponses,
          sensory_notes: sensoryNotes[categoryName] || null,
        });
        console.log(`Saved ${categoryName} for area ${currentAreaId}`);
        toast.success(`${categoryName} saved!`);
        
        if (currentCategory < categories.length - 1) {
          setCurrentCategory(currentCategory + 1);
        } else {
          setStep(4);
        }
      } catch (error) {
        console.error('Save error:', error);
        toast.error(`Failed to save ${categoryName}`);
      }
    } else if (step === 4) {
      await saveArea();
      toast.success('Area created successfully. You can now add another area or complete the sensory assessment.');
      navigate(`/venue/${venueId}`);
    }
  };

  const handleBack = () => {
    if (step === 3 && currentCategory > 0) {
      setCurrentCategory(currentCategory - 1);
    } else if (step > 1) {
      setStep(step - 1);
    } else {
      navigate(`/venue/${venueId}`);
    }
  };

  if (!prompts) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const categoryName = categories[currentCategory];
  const allCategoryPrompts = prompts[categoryName] || [];
  const categoryPrompts = filterPromptsByEnvironment(allCategoryPrompts, areaData.area_environment);
  const answeredCount = categoryPrompts.filter(p => 
    responses[`${categoryName}_${p.id}`] !== undefined && 
    responses[`${categoryName}_${p.id}`] !== null
  ).length;

  const descriptionWordCount = countWords(areaData.description);
  const descriptionError = descriptionWordCount > 250;

  return (
    <div className="min-h-screen p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-6 rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {isLocked && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <Lock className="w-6 h-6 text-emerald-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-emerald-900">Submission Complete – This Assessment is Now Locked</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  This area's sensory assessment has been submitted and paid. The data is now read-only and cannot be edited.
                </p>
                <Button
                  onClick={() => navigate(`/venue/${venueId}`)}
                  variant="outline"
                  className="mt-4 rounded-full"
                >
                  Return to Venue
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-card rounded-xl border border-border/50 shadow-lg p-8">
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    s < step
                      ? 'bg-emerald-600 text-white'
                      : s === step
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
              ))}
            </div>

            <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="area-builder-title">
              {step === 1 && 'Area Details'}
              {step === 2 && 'Photos'}
              {step === 3 && (CATEGORY_HEADERS[categoryName]?.title || categoryName)}
              {step === 4 && 'Support Indicators'}
            </h1>
            {step === 3 && (
              <div className="space-y-4">
                {/* Professional auditor-style category header */}
                {(() => {
                  const categoryHeader = CATEGORY_HEADERS[categoryName];
                  if (!categoryHeader) return null;
                  const IconComponent = categoryHeader.icon;
                  return (
                    <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-4 mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        {IconComponent && <IconComponent className="w-5 h-5 text-primary" />}
                        <span className="font-semibold text-primary">
                          {categoryHeader.question}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {categoryHeader.helper}
                      </p>
                    </div>
                  );
                })()}
                
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    Category {currentCategory + 1} of {categories.length} • {answeredCount}/{categoryPrompts.length} answered
                  </p>
                  {areaData.area_environment && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      areaData.area_environment === 'Indoor' ? 'bg-blue-100 text-blue-800' :
                      areaData.area_environment === 'Outdoor' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {areaData.area_environment} Area
                    </span>
                  )}
                </div>
                
                {/* Assessment Intro - only show on first category */}
                {currentCategory === 0 && assessmentIntro[areaData.area_environment] && (
                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      {assessmentIntro[areaData.area_environment]}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="area-name" className="text-base font-medium">Area Name *</Label>
                <Input
                  id="area-name"
                  placeholder="e.g., Main Dining Room"
                  value={areaData.name}
                  onChange={(e) => setAreaData({ ...areaData, name: e.target.value })}
                  required
                  disabled={isLocked}
                  data-testid="area-name-input"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="area-environment" className="text-base font-medium">Area Environment *</Label>
                <Select 
                  value={areaData.area_environment} 
                  onValueChange={(value) => setAreaData({ ...areaData, area_environment: value })}
                  disabled={isLocked}
                >
                  <SelectTrigger className="h-12" data-testid="area-environment-select">
                    <SelectValue placeholder="Select environment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {AREA_ENVIRONMENTS.map(env => (
                      <SelectItem key={env} value={env}>{env}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This determines which questions will be shown for this area
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="area-description" className="text-base font-medium">Description (optional)</Label>
                  <span className={`text-xs ${descriptionError ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                    {descriptionWordCount}/250 words
                  </span>
                </div>
                <Textarea
                  id="area-description"
                  placeholder="Brief description of this area"
                  value={areaData.description}
                  onChange={(e) => setAreaData({ ...areaData, description: e.target.value })}
                  disabled={isLocked}
                  data-testid="area-description-input"
                  className="min-h-[100px]"
                  rows={4}
                />
                {descriptionError && (
                  <p className="text-xs text-destructive">Description exceeds 250 word limit</p>
                )}
              </div>

              {/* Observations Field - Critical for auditor-style assessment */}
              <div className="space-y-2 pt-4 border-t">
                <Label htmlFor="area-observations" className="text-base font-medium">
                  Observations and specific considerations
                </Label>
                <Textarea
                  id="area-observations"
                  placeholder="Include anything that may affect the experience, such as specific times, events, or unusual sensory triggers..."
                  value={areaData.observations || ''}
                  onChange={(e) => setAreaData({ ...areaData, observations: e.target.value })}
                  disabled={isLocked}
                  data-testid="area-observations-input"
                  className="min-h-[100px]"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Note any specific details that may affect the sensory experience (e.g., "Gets louder after 3pm when school finishes", "Strong chlorine smell near pool").
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Upload up to 3 photos for this area (JPEG/PNG/HEIC/WebP, max 10MB each)
              </p>
              
              {areaData.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {areaData.photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border group">
                      <img src={photo.url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {/* Move Left Button */}
                        {index > 0 && (
                          <button
                            onClick={() => handleMovePhoto(index, 'left')}
                            className="bg-white text-gray-800 rounded-full p-2 hover:bg-gray-100"
                            type="button"
                            title="Move left"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                        )}
                        {/* Delete Button */}
                        {!isLocked && (
                        <button
                          onClick={() => handleDeletePhoto(index)}
                          className="bg-destructive text-destructive-foreground rounded-full p-2 hover:bg-destructive/90"
                          type="button"
                          title="Delete photo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                        {/* Move Right Button */}
                        {index < areaData.photos.length - 1 && (
                          <button
                            onClick={() => handleMovePhoto(index, 'right')}
                            className="bg-white text-gray-800 rounded-full p-2 hover:bg-gray-100"
                            type="button"
                            title="Move right"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {index + 1} of {areaData.photos.length}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {areaData.photos.length < 3 && !isLocked && (
                <div
                  className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => !uploading && document.getElementById('photo-upload')?.click()}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-sm font-medium">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium">Click to upload photos</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {areaData.photos.length}/3 uploaded • Max 10MB
                      </p>
                    </>
                  )}
                  <input
                    key={uploadKey}
                    id="photo-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/heic,image/heif,image/webp,.jpg,.jpeg,.png,.heic,.heif,.webp"
                    className="hidden"
                    disabled={uploading || isLocked}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handlePhotoUpload(file);
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8">
              {categoryPrompts.map((prompt, index) => (
                <div key={prompt.id} className="space-y-4 pb-6 border-b border-border/50 last:border-0 last:pb-0">
                  <div>
                    <Label className="text-base font-medium leading-relaxed">
                      {index + 1}. {prompt.text}
                    </Label>
                    {prompt.hint && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {prompt.hint}
                      </p>
                    )}
                  </div>
                  <RadioGroup
                    value={responses[`${categoryName}_${prompt.id}`] !== undefined ? String(responses[`${categoryName}_${prompt.id}`]) : ''}
                    onValueChange={(value) =>
                      setResponses({
                        ...responses,
                        [`${categoryName}_${prompt.id}`]: parseInt(value),
                      })
                    }
                    disabled={isLocked}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                    data-testid={`prompt-${prompt.id}`}
                  >
                    {prompt.options?.map((option) => (
                      <div
                        key={option.score}
                        className={`flex items-center space-x-3 p-4 border rounded-xl transition-all ${
                          responses[`${categoryName}_${prompt.id}`] === option.score
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                            : isLocked 
                              ? 'opacity-60 cursor-not-allowed' 
                              : 'hover:bg-secondary/50 hover:border-border'
                        }`}
                      >
                        <RadioGroupItem
                          value={String(option.score)}
                          id={`${prompt.id}-${option.score}`}
                          disabled={isLocked}
                        />
                        <Label
                          htmlFor={`${prompt.id}-${option.score}`}
                          className={`flex-1 font-normal ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className="font-medium">{getOptionDisplayLabel(option.label)}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
              
              {/* Venue-Specific Sensory Notes */}
              <div className="pt-4 border-t mt-6">
                <Label htmlFor="sensory-notes" className="text-base font-medium mb-2 block">
                  Additional venue-specific sensory details
                  <span className="text-xs text-muted-foreground ml-2">(Optional)</span>
                </Label>
                <textarea
                  id="sensory-notes"
                  className="w-full min-h-[80px] p-3 border rounded-lg text-sm resize-y bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder={`Any specific details about ${categoryName} at this venue that aren't captured above...`}
                  value={sensoryNotes[categoryName] || ''}
                  onChange={(e) => setSensoryNotes({
                    ...sensoryNotes,
                    [categoryName]: e.target.value
                  })}
                  disabled={isLocked}
                  data-testid="sensory-notes-input"
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground mb-4">
                Select the support features available in this area:
              </p>

              {/* Physical */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wide">Physical</h4>
                {[
                  { key: 'seating_available', label: 'Seating Available' },
                  { key: 'step_free_access', label: 'Step-Free Access' },
                  { key: 'accessible_toilet_nearby', label: 'Accessible Toilet Nearby' },
                ].map((indicator) => (
                  <div
                    key={indicator.key}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <Label htmlFor={indicator.key} className="cursor-pointer font-medium text-sm">
                      {indicator.label}
                    </Label>
                    <Switch
                      id={indicator.key}
                      checked={areaData.support_indicators[indicator.key]}
                      onCheckedChange={(checked) =>
                        setAreaData({
                          ...areaData,
                          support_indicators: {
                            ...areaData.support_indicators,
                            [indicator.key]: checked,
                          },
                        })
                      }
                      data-testid={`indicator-${indicator.key}`}
                      disabled={isLocked}
                    />
                  </div>
                ))}
              </div>

              {/* Staff */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wide">Staff</h4>
                {[
                  { key: 'staff_present', label: 'Staff Present in Area' },
                  { key: 'assistance_available', label: 'Assistance Available on Request' },
                ].map((indicator) => (
                  <div
                    key={indicator.key}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <Label htmlFor={indicator.key} className="cursor-pointer font-medium text-sm">
                      {indicator.label}
                    </Label>
                    <Switch
                      id={indicator.key}
                      checked={areaData.support_indicators[indicator.key]}
                      onCheckedChange={(checked) =>
                        setAreaData({
                          ...areaData,
                          support_indicators: {
                            ...areaData.support_indicators,
                            [indicator.key]: checked,
                          },
                        })
                      }
                      data-testid={`indicator-${indicator.key}`}
                      disabled={isLocked}
                    />
                  </div>
                ))}
              </div>

              {/* Sensory */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wide">Sensory</h4>
                {[
                  { key: 'quiet_space_nearby', label: 'Quiet Space Nearby' },
                  { key: 'lower_lighting_available', label: 'Lower Lighting Available' },
                  { key: 'calm_waiting_area', label: 'Calm Waiting Area' },
                ].map((indicator) => (
                  <div
                    key={indicator.key}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <Label htmlFor={indicator.key} className="cursor-pointer font-medium text-sm">
                      {indicator.label}
                    </Label>
                    <Switch
                      id={indicator.key}
                      checked={areaData.support_indicators[indicator.key]}
                      onCheckedChange={(checked) =>
                        setAreaData({
                          ...areaData,
                          support_indicators: {
                            ...areaData.support_indicators,
                            [indicator.key]: checked,
                          },
                        })
                      }
                      data-testid={`indicator-${indicator.key}`}
                      disabled={isLocked}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wide">Navigation</h4>
                {[
                  { key: 'clear_signage', label: 'Clear Signage' },
                  { key: 'map_available', label: 'Map Available' },
                ].map((indicator) => (
                  <div
                    key={indicator.key}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <Label htmlFor={indicator.key} className="cursor-pointer font-medium text-sm">
                      {indicator.label}
                    </Label>
                    <Switch
                      id={indicator.key}
                      checked={areaData.support_indicators[indicator.key]}
                      onCheckedChange={(checked) =>
                        setAreaData({
                          ...areaData,
                          support_indicators: {
                            ...areaData.support_indicators,
                            [indicator.key]: checked,
                          },
                        })
                      }
                      data-testid={`indicator-${indicator.key}`}
                      disabled={isLocked}
                    />
                  </div>
                ))}
              </div>

              {/* Access Control */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wide">Access Control</h4>
                {[
                  { key: 'timed_entry_available', label: 'Timed Entry Available' },
                  { key: 'managed_queue_system', label: 'Managed Queue System' },
                ].map((indicator) => (
                  <div
                    key={indicator.key}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <Label htmlFor={indicator.key} className="cursor-pointer font-medium text-sm">
                      {indicator.label}
                    </Label>
                    <Switch
                      id={indicator.key}
                      checked={areaData.support_indicators[indicator.key]}
                      onCheckedChange={(checked) =>
                        setAreaData({
                          ...areaData,
                          support_indicators: {
                            ...areaData.support_indicators,
                            [indicator.key]: checked,
                          },
                        })
                      }
                      data-testid={`indicator-${indicator.key}`}
                      disabled={isLocked}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isLocked && (
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="rounded-full"
                data-testid="step-back-button"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={step === 1 && descriptionError}
              className="flex-1 h-12 rounded-full font-medium"
              data-testid="next-button"
            >
              {step === 4 ? 'Complete Area' : step === 3 && currentCategory === categories.length - 1 ? 'Finish Assessment' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AreaBuilder;